import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BarChart3, Check, Settings, ShieldCheck, X } from 'lucide-react';
import { getStoredConsent, hasConsentDecision, saveConsent } from '../lib/consent';

export default function CookieConsentBanner() {
  const { t } = useTranslation('pages');
  const [visible, setVisible] = useState(false);
  const [showPreferences, setShowPreferences] = useState(false);
  const [analyticsEnabled, setAnalyticsEnabled] = useState(false);

  useEffect(() => {
    const consent = getStoredConsent();
    setAnalyticsEnabled(consent.analytics);
    setVisible(!hasConsentDecision());
  }, []);

  useEffect(() => {
    const openPreferences = () => {
      const consent = getStoredConsent();
      setAnalyticsEnabled(consent.analytics);
      setShowPreferences(true);
      setVisible(true);
    };

    window.addEventListener('aprendos:openconsent', openPreferences);
    return () => window.removeEventListener('aprendos:openconsent', openPreferences);
  }, []);

  if (!visible) {
    return null;
  }

  const saveAndClose = (analytics) => {
    const previousConsent = getStoredConsent();
    saveConsent({ analytics });

    if (previousConsent.analytics && !analytics) {
      window.location.reload();
      return;
    }

    setVisible(false);
    setShowPreferences(false);
  };

  return (
    <div className="fixed inset-x-0 bottom-0 z-[100] px-4 pb-4 sm:px-6 sm:pb-6 pointer-events-none">
      <section
        className="pointer-events-auto mx-auto max-w-5xl rounded-lg border border-stone-200 bg-[#FDFBF7] shadow-2xl shadow-stone-950/15"
        aria-label="Cookie consent"
      >
        <div className="grid gap-4 p-4 sm:p-5 lg:grid-cols-[1fr_auto] lg:items-center">
          <div className="flex gap-3">
            <div className="mt-1 flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-emerald-50 text-emerald-700">
              <ShieldCheck size={20} aria-hidden="true" />
            </div>
            <div>
              <h2 className="font-body text-base font-bold text-stone-950">
                {t('legal.consent.title', 'Privacy preferences')}
              </h2>
              <p className="mt-1 max-w-3xl font-body text-sm font-semibold leading-6 text-stone-700">
                {t('legal.consent.body', 'We use essential storage to keep Aprendos working. With your permission, we also use Google Analytics to understand visits and improve the product.')}
              </p>
            </div>
          </div>

          <div className="flex flex-col gap-2 xs:flex-row lg:justify-end">
            <button
              type="button"
              onClick={() => saveAndClose(false)}
              className="inline-flex min-h-11 items-center justify-center gap-2 rounded-md border border-stone-300 px-4 text-sm font-bold text-stone-800 transition hover:bg-stone-100 focus:outline-none focus:ring-2 focus:ring-stone-900 focus:ring-offset-2"
            >
              <X size={16} aria-hidden="true" />
              {t('legal.consent.essentialOnly', 'Essential only')}
            </button>
            <button
              type="button"
              onClick={() => setShowPreferences((current) => !current)}
              className="inline-flex min-h-11 items-center justify-center gap-2 rounded-md border border-stone-300 px-4 text-sm font-bold text-stone-800 transition hover:bg-stone-100 focus:outline-none focus:ring-2 focus:ring-stone-900 focus:ring-offset-2"
              aria-expanded={showPreferences}
            >
              <Settings size={16} aria-hidden="true" />
              {t('legal.consent.customize', 'Customize')}
            </button>
            <button
              type="button"
              onClick={() => saveAndClose(true)}
              className="inline-flex min-h-11 items-center justify-center gap-2 rounded-md bg-stone-950 px-4 text-sm font-bold text-white transition hover:bg-stone-800 focus:outline-none focus:ring-2 focus:ring-stone-900 focus:ring-offset-2"
            >
              <Check size={16} aria-hidden="true" />
              {t('legal.consent.acceptAnalytics', 'Accept analytics')}
            </button>
          </div>
        </div>

        {showPreferences && (
          <div className="border-t border-stone-200 p-4 sm:p-5">
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="rounded-lg border border-stone-200 bg-white p-4">
                <div className="flex items-start gap-3">
                  <ShieldCheck className="mt-0.5 text-emerald-700" size={18} aria-hidden="true" />
                  <div>
                    <h3 className="font-body text-sm font-bold text-stone-950">{t('legal.consent.essentialTitle', 'Essential')}</h3>
                    <p className="mt-1 text-sm font-semibold leading-6 text-stone-600">
                      {t('legal.consent.essentialBody', 'Required for security, authentication, checkout, and core site behavior.')}
                    </p>
                    <p className="mt-3 text-sm font-bold text-emerald-700">{t('legal.consent.alwaysOn', 'Always on')}</p>
                  </div>
                </div>
              </div>

              <label className="rounded-lg border border-stone-200 bg-white p-4">
                <div className="flex items-start gap-3">
                  <BarChart3 className="mt-0.5 text-sky-700" size={18} aria-hidden="true" />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center justify-between gap-4">
                      <h3 className="font-body text-sm font-bold text-stone-950">{t('legal.consent.analyticsTitle', 'Analytics')}</h3>
                      <input
                        type="checkbox"
                        checked={analyticsEnabled}
                        onChange={(event) => setAnalyticsEnabled(event.target.checked)}
                        className="h-5 w-5 rounded border-stone-300 accent-stone-950"
                      />
                    </div>
                    <p className="mt-1 text-sm font-semibold leading-6 text-stone-600">
                      {t('legal.consent.analyticsBody', 'Helps us measure page views, product usage, and campaign performance with Google Analytics.')}
                    </p>
                  </div>
                </div>
              </label>
            </div>

            <div className="mt-4 flex flex-col gap-2 xs:flex-row xs:justify-end">
              <button
                type="button"
                onClick={() => saveAndClose(false)}
                className="inline-flex min-h-11 items-center justify-center rounded-md border border-stone-300 px-4 text-sm font-bold text-stone-800 transition hover:bg-stone-100 focus:outline-none focus:ring-2 focus:ring-stone-900 focus:ring-offset-2"
              >
                {t('legal.consent.rejectOptional', 'Reject optional')}
              </button>
              <button
                type="button"
                onClick={() => saveAndClose(analyticsEnabled)}
                className="inline-flex min-h-11 items-center justify-center rounded-md bg-stone-950 px-4 text-sm font-bold text-white transition hover:bg-stone-800 focus:outline-none focus:ring-2 focus:ring-stone-900 focus:ring-offset-2"
              >
                {t('legal.consent.savePreferences', 'Save preferences')}
              </button>
            </div>
          </div>
        )}
      </section>
    </div>
  );
}
